import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, X, FileUp, FileText, CheckCircle2, AlertTriangle, Download, Info } from 'lucide-react';
import * as XLSX from 'xlsx';

// Columnas del template — las primeras 3 son las únicas realmente obligatorias
const TEMPLATE_HEADERS = [
  // Generales
  'Tipo Contacto',   // CLIENT | SUPPLIER | FACTORY
  'Nombre',          // obligatorio
  'Nombre Fantasía',
  'Email',
  'Teléfono',
  'Móvil',
  'Cargo',
  'Estado',          // Activo | Inactivo | Borrador
  'País',
  'Dirección',
  'Dirección Facturación',
  'Código Postal',
  'Ciudad',
  'Comprador',
  'Sitio Web',
  'ID Wechat',
  'Comentarios',
  // Específicos FACTORY / SUPPLIER
  'Puerto Fábrica',  // nombre del puerto (debe existir en el sistema)
  'Término de Pago',
  'Divisa Proveedor',
  'MOQ Mínimo',
  'Tiempo de Entrega (días)',
];

const EXAMPLE_ROWS = [
  [
    'CLIENT', 'Empresa Ejemplo SA', 'Ejemplo', 'contacto@ejemplo.com',
    '+54 11 1234-5678', '', 'Gerente', 'Activo',
    'Argentina', 'Av. Corrientes 1234', '', '1043', 'Buenos Aires',
    'Juan Pérez', 'www.ejemplo.com', '', 'Cliente desde 2022',
    '', '', '', '', '',
  ],
  [
    'FACTORY', 'Fábrica China XYZ', 'XYZ Factory', 'factory@xyz.cn',
    '+86 755 1234 5678', '', 'Sales Manager', 'Activo',
    'China', 'Shenzhen Industrial Zone 123', '', '518000', 'Shenzhen',
    '', 'www.xyzfactory.cn', 'WeChatXYZ', '',
    'Ningbo', '30% adelanto / 70% contra BL', 'USD', '1000', '45',
  ],
];

const VALID_TYPES   = ['CLIENT', 'SUPPLIER', 'FACTORY'];
const VALID_STATUSES = ['Activo', 'Inactivo', 'Borrador', 'Potencial', 'Lead', 'Prospecto', 'Cliente'];

const ContactImporter = ({
  onImportContacts = () => {},
  companies = [],
  ports = [],
  onClose = () => {},
}) => {
  const [file, setFile]                     = useState(null);
  const [data, setData]                     = useState([]);
  const [errors, setErrors]                 = useState([]);
  const [warnings, setWarnings]             = useState([]);
  const [importSuccess, setImportSuccess]   = useState(false);
  const fileInputRef = useRef(null);

  // ── Descarga plantilla con filas de ejemplo ────────────────────────────────
  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...EXAMPLE_ROWS]);
    // Ancho de columnas
    ws['!cols'] = TEMPLATE_HEADERS.map((h) => ({ wch: Math.max(h.length + 2, 18) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contactos');
    XLSX.writeFile(wb, 'plantilla_contactos.xlsx');
  };

  // ── Procesa el archivo ────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setFile(f); setData([]); setErrors([]); setWarnings([]); setImportSuccess(false);
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb   = XLSX.read(ev.target.result, { type: 'binary' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (!json.length) { setErrors([{ row: 'General', message: 'Archivo vacío.' }]); return; }

        const fileHeaders = json[0].map(h => String(h).trim());
        const rows = json.slice(1);
        parseRows(fileHeaders, rows);
      } catch (err) {
        setErrors([{ row: 'General', message: `Error al leer el archivo: ${err.message}` }]);
      }
    };
    reader.readAsBinaryString(f);
  };

  // ── Parseo y validación fila por fila ────────────────────────────────────
  const parseRows = (fileHeaders, rows) => {
    const col = (row, name) => {
      const idx = fileHeaders.indexOf(name);
      return idx >= 0 ? (row[idx] !== undefined && row[idx] !== null ? String(row[idx]).trim() : '') : '';
    };

    const newErrors = [], newWarnings = [], contacts = [];

    rows.forEach((row, i) => {
      const rowNum = i + 2;
      const name = col(row, 'Nombre');
      if (!name) return; // fila vacía, omitir sin error

      const contactType = col(row, 'Tipo Contacto').toUpperCase() || 'CLIENT';
      if (!VALID_TYPES.includes(contactType)) {
        newErrors.push({ row: rowNum, message: `Tipo Contacto inválido: "${col(row, 'Tipo Contacto')}". Use: ${VALID_TYPES.join(', ')}.` });
        return;
      }

      const statusRaw = col(row, 'Estado');
      let status = statusRaw;
      if (statusRaw && !VALID_STATUSES.includes(statusRaw)) {
        newWarnings.push({ row: rowNum, message: `Estado "${statusRaw}" no reconocido, se usará "Activo".` });
        status = 'Activo';
      }
      if (!status) status = 'Activo';

      // Resolver Puerto Fábrica por nombre
      const portName = col(row, 'Puerto Fábrica');
      let factoryPortId = '';
      if (portName) {
        const found = ports.find(p => p.name?.toLowerCase().trim() === portName.toLowerCase().trim());
        if (found) {
          factoryPortId = String(found.id);
        } else {
          newWarnings.push({ row: rowNum, message: `Puerto Fábrica "${portName}" no encontrado en el sistema — se importará sin puerto.` });
        }
      }

      contacts.push({
        name,
        contactType,
        fantasyName:          col(row, 'Nombre Fantasía'),
        email:                col(row, 'Email'),
        phone:                col(row, 'Teléfono'),
        mobile:               col(row, 'Móvil'),
        position:             col(row, 'Cargo'),
        status,
        country:              col(row, 'País'),
        address:              col(row, 'Dirección'),
        billingAddress:       col(row, 'Dirección Facturación'),
        postalCode:           col(row, 'Código Postal'),
        city:                 col(row, 'Ciudad'),
        comprador:            col(row, 'Comprador'),
        website:              col(row, 'Sitio Web'),
        wechatId:             col(row, 'ID Wechat'),
        description:          col(row, 'Comentarios'),
        factoryPortId,
        paymentTerm:          col(row, 'Término de Pago'),
        supplierCurrency:     col(row, 'Divisa Proveedor') || 'USD',
        moqMinimum:           col(row, 'MOQ Mínimo'),
        leadTimeDays:         col(row, 'Tiempo de Entrega (días)'),
        lastContact:          new Date(),
        companyIds:           [],
      });
    });

    setErrors(newErrors);
    setWarnings(newWarnings);
    setData(contacts);
  };

  const handleImport = () => {
    if (errors.length === 0 && data.length > 0) {
      onImportContacts(data);
      setImportSuccess(true);
      setTimeout(onClose, 2000);
    }
  };

  const factoryCount  = data.filter(d => d.contactType === 'FACTORY').length;
  const supplierCount = data.filter(d => d.contactType === 'SUPPLIER').length;
  const clientCount   = data.filter(d => d.contactType === 'CLIENT').length;

  return (
    <motion.div
      className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
    >
      <div className="w-full max-h-[88vh] overflow-y-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileUp className="w-8 h-8 text-blue-500" />
            <div>
              <h3 className="text-2xl font-bold text-gray-800">Importar Contactos desde Excel</h3>
              <p className="text-sm text-gray-500">Soporta Clientes, Agentes y Fábricas. Solo el campo <strong>Nombre</strong> es obligatorio.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {importSuccess ? (
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
            className="text-center bg-green-50 text-green-700 p-6 rounded-xl flex flex-col items-center gap-4">
            <CheckCircle2 className="w-16 h-16 text-green-500" />
            <h3 className="text-xl font-bold">¡Importación Exitosa!</h3>
            <p>Se importaron {data.length} contacto{data.length !== 1 ? 's' : ''}.</p>
            <button onClick={onClose} className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600">Cerrar</button>
          </motion.div>
        ) : (
          <>
            {/* Info del template */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3 text-sm text-blue-800">
              <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold mb-1">Columnas soportadas</p>
                <p>Tipo Contacto: <strong>CLIENT</strong>, <strong>SUPPLIER</strong>, <strong>FACTORY</strong> · Estado: Activo, Inactivo, Borrador · Puerto Fábrica: debe coincidir con un puerto existente en el sistema.</p>
              </div>
            </div>

            {/* Descarga plantilla */}
            <button onClick={downloadTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium">
              <Download className="w-4 h-4" /> Descargar plantilla con ejemplos
            </button>

            {/* Drop zone */}
            <div
              className="p-6 border-2 border-dashed border-gray-300 rounded-xl text-center cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => fileInputRef.current.click()}
            >
              <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".xlsx,.xls" className="hidden" />
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              {file
                ? <p className="text-gray-700 font-medium">{file.name}</p>
                : <p className="text-gray-700 font-medium">Arrastrá tu archivo Excel aquí o hacé clic para seleccionar</p>}
              <p className="text-xs text-gray-400 mt-1">.xlsx / .xls</p>
            </div>

            {/* Errores */}
            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl space-y-2">
                <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-5 h-5" /> Errores que impiden la importación</div>
                <ul className="list-disc pl-5 space-y-1">
                  {errors.map((e, i) => <li key={i} className="text-sm">{e.row !== 'General' && <strong>Fila {e.row}: </strong>}{e.message}</li>)}
                </ul>
              </div>
            )}

            {/* Advertencias */}
            {warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl space-y-2">
                <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-5 h-5" /> Advertencias (se importará igual)</div>
                <ul className="list-disc pl-5 space-y-1">
                  {warnings.map((w, i) => <li key={i} className="text-sm"><strong>Fila {w.row}: </strong>{w.message}</li>)}
                </ul>
              </div>
            )}

            {/* Resumen */}
            {data.length > 0 && errors.length === 0 && (
              <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-xl">
                <div className="flex items-center gap-2 font-semibold mb-2"><CheckCircle2 className="w-5 h-5" /> {data.length} contacto{data.length !== 1 ? 's' : ''} listos para importar</div>
                <div className="flex gap-4 text-sm">
                  {clientCount  > 0 && <span>👤 {clientCount} Cliente{clientCount !== 1 ? 's' : ''}</span>}
                  {supplierCount > 0 && <span>🤝 {supplierCount} Agente{supplierCount !== 1 ? 's' : ''}</span>}
                  {factoryCount  > 0 && <span>🏭 {factoryCount} Fábrica{factoryCount !== 1 ? 's' : ''}</span>}
                </div>
              </div>
            )}

            {/* Botones */}
            <div className="flex justify-end gap-3">
              <button onClick={onClose} className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cerrar</button>
              <button
                onClick={handleImport}
                disabled={data.length === 0 || errors.length > 0}
                className={`px-6 py-3 rounded-lg font-semibold transition-colors ${data.length === 0 || errors.length > 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              >
                <Upload className="w-4 h-4 inline mr-2" />
                Importar {data.length > 0 ? `(${data.length})` : ''}
              </button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default ContactImporter;
